import {
  createListenerMiddleware,
  type TypedStartListening,
} from '@reduxjs/toolkit';

import type { AppDispatch, RootState } from 'features/app/store/store';
import {
  callIdSelector,
  isSameActiveCallId,
} from 'features/call/call-base/store/selectors';

import {
  selectIsSignMailSessionInProgress,
  signMailCallIdSelector,
} from './signMailSelectors';
import { preFetchedUploadCleared } from './signMailSlice';
import { resetSignMailState } from './thunks';

// resetCall already clears SignMail inside performResets. Matching the action
// type avoids importing the call slice, which imports this feature's barrel.
const RESET_CALL_ACTION_TYPE = 'call/resetCall';

const hasStaleSignMailSession = (state: RootState) =>
  selectIsSignMailSessionInProgress(state) &&
  !isSameActiveCallId(callIdSelector(state), signMailCallIdSelector(state));

const hasPreFetchedUpload = (state: RootState) =>
  state.signMail.preFetchedUpload !== undefined;

const listenerMiddleware = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;
const startListening = listenerMiddleware.startListening as AppStartListening;

startListening({
  predicate: (action, currentState, previousState) => {
    if (action.type === RESET_CALL_ACTION_TYPE) {
      return false;
    }

    if (callIdSelector(currentState) === callIdSelector(previousState)) {
      return false;
    }

    return (
      hasStaleSignMailSession(currentState) || hasPreFetchedUpload(currentState)
    );
  },
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState();
    if (hasStaleSignMailSession(state)) {
      listenerApi.dispatch(resetSignMailState());
      return;
    }

    listenerApi.dispatch(preFetchedUploadCleared());
  },
});

export const signMailCallIdListenerMiddleware = listenerMiddleware.middleware;
